import type { ComponentPropsWithRef } from 'react';

import { Icon, type IconName } from '@/components/ui/icon';
import { cn } from '@/lib/cn';

export interface InputProps extends Omit<ComponentPropsWithRef<'input'>, 'size'> {
  /** Marks the field as invalid and switches it to the danger palette. */
  invalid?: boolean;
  iconLeft?: IconName;
}

/** Shared between Input, Select and Textarea so every control lines up. */
export const CONTROL_BASE =
  'w-full rounded-sm border bg-surface text-content placeholder:text-content-subtle ' +
  'chrome-input transition-colors duration-75 disabled:cursor-not-allowed ' +
  'disabled:bg-surface-muted disabled:text-content-subtle';

/**
 * The height, padding and type size every single-line control uses.
 *
 * Exported rather than repeated, because it had already drifted: Select,
 * DatePicker and SearchInput each carried their own `h-8 px-2.5 text-sm`,
 * which put them 4px taller than the Input beside them on a desktop form and
 * 4px shorter on a phone - and in a different type size either way. Anything
 * that renders as a field takes this, so a row of mixed controls lines up by
 * construction rather than by everyone remembering the same three numbers.
 *
 * Responsive on purpose: touch targets grow below `sm`.
 */
export const CONTROL_SIZE = 'h-9 px-2 text-xs sm:h-7';

export function Input({ invalid, iconLeft, className, ...props }: InputProps) {
  const control = (
    <input
      aria-invalid={invalid ? true : undefined}
      className={cn(
        CONTROL_BASE,
        CONTROL_SIZE,
        invalid ? 'border-danger' : 'border-border-strong hover:border-content-subtle',
        iconLeft && 'pl-7',
        className,
      )}
      {...props}
    />
  );

  if (!iconLeft) {
    return control;
  }

  return (
    <div className="relative">
      <Icon
        name={iconLeft}
        size={14}
        className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-content-subtle"
      />
      {control}
    </div>
  );
}
